import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, Reorder, useDragControls } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { api, fmt, downloadCsv, monthLabel } from "@/lib/api";
import { getCategoryIcon } from "@/lib/icons";
import { CategoryDialog, DeleteCategoryButton } from "@/components/CategoryDialog";
import { Sparkline } from "@/components/Sparkline";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Wallet, TrendingDown, PiggyBank, AlertTriangle, Plus, Download, ChevronDown, Pencil, Trash2, CornerDownRight, GripVertical } from "lucide-react";

const SummaryCard = ({ label, value, sub, icon: Icon, accent, testid, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: i * 0.05 }}
    className="relative overflow-hidden rounded-2xl bg-[#141a26] border border-[#1f2a3d] p-5"
  >
    <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: accent, boxShadow: `0 0 16px ${accent}` }} />
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold tracking-[0.14em] text-slate-500 uppercase">{label}</span>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div data-testid={testid} className="font-mono-num text-3xl font-bold tracking-tight mt-3 text-slate-50">{value}</div>
    <p className="text-xs text-slate-500 mt-1">{sub}</p>
  </motion.div>
);

const CategoryCard = ({ node, txnCount, spark, sparkMonths }) => {
  const [open, setOpen] = React.useState(false);
  const qc = useQueryClient();
  const controls = useDragControls();
  const Icon = getCategoryIcon(node.name);
  const pct = node.group_effective > 0 ? Math.min(999, Math.round((node.group_actual / node.group_effective) * 100)) : 0;
  const over = node.over_budget;
  const warning = node.warning;
  const statusColor = over ? "#ef4444" : warning ? "#f97316" : "#22c55e";
  const hasSubs = node.subcategories?.length > 0;

  const invalidate = () => qc.invalidateQueries();
  const createMut = useMutation({ mutationFn: api.createCategory, onSuccess: () => { invalidate(); toast.success("Sub-category added"); } });
  const updateMut = useMutation({ mutationFn: ({ id, data }) => api.updateCategory(id, data), onSuccess: () => { invalidate(); toast.success("Saved"); } });
  const deleteMut = useMutation({ mutationFn: api.deleteCategory, onSuccess: () => { invalidate(); toast.success("Deleted"); } });

  return (
    <Reorder.Item
      value={node.id}
      dragListener={false}
      dragControls={controls}
      id={`cat-card-${node.id}`}
      data-testid="category-row"
      className="rounded-2xl bg-[#141a26] border border-[#1f2a3d] p-4 flex flex-col gap-3 transition-shadow hover:border-[#2a364f]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button
            data-testid={`drag-cat-${node.id}`}
            onPointerDown={(e) => controls.start(e)}
            className="cursor-grab active:cursor-grabbing text-slate-600 hover:text-slate-400 touch-none -ml-1"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: node.color + "22", color: node.color }}>
            <Icon style={{ width: 18, height: 18 }} />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-semibold text-sm truncate">{node.name}</div>
            <div className="text-[11px] text-slate-500">{txnCount} transaction{txnCount === 1 ? "" : "s"}</div>
          </div>
        </div>
        <button data-testid={`expand-cat-${node.id}`} onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs font-mono-num shrink-0" style={{ color: statusColor }}>
          {pct}%
          <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} />
        </button>
      </div>

      <div className="flex items-end justify-between">
        <span className="font-mono-num text-2xl font-bold text-slate-50">{fmt(node.group_actual)}</span>
        <span className="text-xs text-slate-500 font-mono-num pb-1">of {fmt(node.group_effective)}</span>
      </div>

      <Progress value={Math.min(100, pct)} className="h-1.5 bg-[#0a0e14]" indicatorStyle={{ background: statusColor }} />

      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid={`sparkline-${node.id}`} className="flex-1 rounded hover:bg-white/5 transition-colors" title="View 6-month totals">
              <Sparkline data={spark} color={statusColor} />
            </button>
          </PopoverTrigger>
          <PopoverContent data-testid={`sparkline-popover-${node.id}`} className="w-56 bg-[#0a0e14] border-[#2a364f] text-slate-100 p-3">
            <div className="text-xs font-semibold text-slate-300 mb-2 flex items-center justify-between">
              <span>{node.name}</span><span className="text-slate-500">6-month spend</span>
            </div>
            <div className="space-y-1">
              {(sparkMonths || []).map((m, i) => (
                <div key={m} className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{monthLabel(m)}</span>
                  <span className="font-mono-num" style={{ color: statusColor }}>{fmt(spark?.[i] || 0)}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <span className="text-[10px] text-slate-600 shrink-0">6&#8209;mo</span>
      </div>

      <div className="flex items-center justify-between text-[11px]">
        <span className="text-slate-500">{fmt(node.group_remaining)} left</span>
        {over && <span data-testid="summary-over-budget-flag" className="flex items-center gap-1 text-red-400 font-medium"><AlertTriangle className="w-3 h-3" /> Over</span>}
        {!over && warning && <span data-testid="category-warning-flag" className="flex items-center gap-1 text-[#f97316] font-medium"><AlertTriangle className="w-3 h-3" /> Near limit</span>}
        {!over && !warning && node.group_carryover !== 0 && <span className="text-[#22c55e]">{node.group_carryover > 0 ? "+" : ""}{fmt(node.group_carryover)} carried</span>}
      </div>

      {open && (
        <div className="border-t border-[#1f2a3d] pt-3 space-y-2">
          {hasSubs && node.subcategories.map((s) => {
            const spct = s.effective > 0 ? Math.round((s.actual / s.effective) * 100) : 0;
            return (
              <div key={s.id} className="flex items-center gap-2 text-xs">
                <CornerDownRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="flex-1 truncate text-slate-300">{s.name}</span>
                <span className="font-mono-num text-slate-500">{fmt(s.actual)}/{fmt(s.effective)} · {spct}%</span>
                <CategoryDialog
                  title="Edit sub-category"
                  initial={s}
                  parentId={node.id}
                  onSubmit={(d) => updateMut.mutate({ id: s.id, data: { name: d.name, proposed: d.proposed, alert_threshold: d.alert_threshold } })}
                  trigger={<button className="text-slate-500 hover:text-[#f97316]"><Pencil className="w-3.5 h-3.5" /></button>}
                />
                <DeleteCategoryButton name={s.name} onConfirm={() => deleteMut.mutate(s.id)} trigger={<button className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>} />
              </div>
            );
          })}
          <div className="flex items-center gap-2 pt-1">
            <CategoryDialog
              title="Edit category"
              initial={node}
              onSubmit={(d) => updateMut.mutate({ id: node.id, data: { name: d.name, proposed: d.proposed, color: d.color, alert_threshold: d.alert_threshold } })}
              trigger={<Button variant="outline" size="sm" data-testid={`edit-category-${node.id}`} className="h-7 px-2 text-xs border-[#2a364f] text-slate-300 hover:bg-[#2a364f] gap-1"><Pencil className="w-3 h-3" /> Edit</Button>}
            />
            <CategoryDialog
              title="Add sub-category"
              parentId={node.id}
              onSubmit={(d) => createMut.mutate(d)}
              trigger={<Button variant="outline" size="sm" data-testid={`add-subcategory-${node.id}`} className="h-7 px-2 text-xs border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/10 gap-1"><Plus className="w-3 h-3" /> Sub</Button>}
            />
            <DeleteCategoryButton
              name={node.name}
              onConfirm={() => deleteMut.mutate(node.id)}
              trigger={<Button variant="outline" size="sm" data-testid={`delete-category-${node.id}`} className="h-7 px-2 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10 gap-1"><Trash2 className="w-3 h-3" /> Delete</Button>}
            />
          </div>
        </div>
      )}
    </Reorder.Item>
  );
};

export default function Dashboard() {
  const { month } = useApp();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard", month], queryFn: () => api.getDashboard(month) });
  const { data: txns = [] } = useQuery({ queryKey: ["transactions", month], queryFn: () => api.getTransactions(month) });
  const { data: sparks } = useQuery({ queryKey: ["sparklines", month], queryFn: () => api.getSparklines(month) });

  const s = data?.summary;
  const cats = React.useMemo(() => data?.categories || [], [data]);
  const nodeById = React.useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);

  const [orderedIds, setOrderedIds] = React.useState([]);
  const saveTimer = React.useRef(null);

  React.useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  React.useEffect(() => {
    const ids = cats.map((c) => c.id);
    setOrderedIds((prev) => {
      const kept = prev.filter((id) => ids.includes(id));
      const added = ids.filter((id) => !kept.includes(id));
      const next = [...kept, ...added];
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) return prev;
      return next;
    });
  }, [cats]);

  const handleReorder = (newIds) => {
    setOrderedIds(newIds);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.reorderCategories(newIds).then(() => {
        qc.invalidateQueries({ queryKey: ["categories"] });
        toast.success("Order saved");
      });
    }, 700);
  };

  const childToRoot = {};
  cats.forEach((n) => { childToRoot[n.id] = n.id; n.subcategories.forEach((sub) => { childToRoot[sub.id] = n.id; }); });
  const countByRoot = {};
  txns.forEach((t) => { const r = childToRoot[t.category_id]; if (r) countByRoot[r] = (countByRoot[r] || 0) + 1; });

  const createMut = useMutation({ mutationFn: api.createCategory, onSuccess: () => { qc.invalidateQueries(); toast.success("Category added"); } });

  const exportBudget = () => {
    if (!cats.length) return toast.error("Nothing to export");
    const header = ["Category", "Type", "Proposed", "Carryover", "Effective Budget", "Actual", "Remaining", "Status"];
    const rows = [header];
    cats.forEach((n) => {
      rows.push([n.name, "Category", n.proposed, n.group_carryover, n.group_effective, n.group_actual, n.group_remaining, n.over_budget ? "Over" : n.warning ? "Near limit" : "OK"]);
      n.subcategories.forEach((sub) => rows.push([`  ${sub.name}`, "Sub-category", sub.proposed, sub.carryover, sub.effective, sub.actual, sub.remaining, sub.over_budget ? "Over" : sub.warning ? "Near limit" : "OK"]));
    });
    downloadCsv(`budget-${month}.csv`, rows);
    toast.success("Budget exported");
  };

  const pctUsed = s?.total_budget > 0 ? Math.round((s.total_spent / s.total_budget) * 100) : 0;
  const orderedNodes = orderedIds.map((id) => nodeById[id]).filter(Boolean);

  return (
    <div className="space-y-8 max-w-[1600px]">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SummaryCard i={0} label="Total Budget" testid="summary-total-budget" value={fmt(s?.total_budget)} sub={`across ${cats.length} categories`} icon={Wallet} accent="#f97316" />
        <SummaryCard i={1} label="Total Spent" testid="summary-amount-spent" value={fmt(s?.total_spent)} sub={`${pctUsed}% of budget used`} icon={TrendingDown} accent="#22c55e" />
        <SummaryCard i={2} label="Remaining" testid="summary-remaining" value={fmt(s?.remaining)} sub={s?.remaining >= 0 ? "left to spend" : "over budget"} icon={PiggyBank} accent="#14b8a6" />
        <SummaryCard i={3} label="Over Budget" testid="summary-over-budget-count" value={`${s?.over_budget_count ?? 0}`} sub={`categor${s?.over_budget_count === 1 ? "y" : "ies"} over limit`} icon={AlertTriangle} accent={s?.over_budget_count ? "#ef4444" : "#22c55e"} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Budget Categories</h2>
            <p className="text-xs text-slate-500 mt-0.5">Drag the grip handle to reorder · expand a card to manage</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="export-budget-button" onClick={exportBudget} variant="outline" className="border-[#2a364f] text-slate-300 hover:bg-[#2a364f] gap-2"><Download className="w-4 h-4" /> Export</Button>
            <CategoryDialog
              title="Add category"
              onSubmit={(d) => createMut.mutate(d)}
              trigger={<Button data-testid="add-category-button" variant="outline" className="border-[#f97316]/50 text-[#f97316] hover:bg-[#f97316]/10 gap-2"><Plus className="w-4 h-4" /> Add Category</Button>}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-slate-500">Loading categories…</div>
        ) : orderedNodes.length === 0 ? (
          <div className="py-16 text-center text-slate-500">No categories yet. Click "Add Category" to start.</div>
        ) : (
          <Reorder.Group as="div" axis="y" values={orderedIds} onReorder={handleReorder} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {orderedNodes.map((n) => (
              <CategoryCard key={n.id} node={n} txnCount={countByRoot[n.id] || 0} spark={sparks?.series?.[n.id] || []} sparkMonths={sparks?.months || []} />
            ))}
          </Reorder.Group>
        )}
      </div>
    </div>
  );
}
